import { useEffect, useMemo, useState } from "react";
import { Receipt, Upload, Download, Trash2, Plus, Loader2, FileText, Wallet } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtMoney(n) {
  return `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function Receipts() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [students, setStudents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    vendor: "", amount: "", category: "curriculum", purchase_date: today(),
    notes: "", student_id: "", file: null,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [r, s, c, st] = await Promise.all([
        api.get(`/receipts?year=${year}`),
        api.get(`/receipts/summary?year=${year}`),
        api.get(`/receipts/categories`),
        api.get(`/students`),
      ]);
      setRows(r.data);
      setSummary(s.data);
      setCategories(c.data.categories);
      setStudents(st.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const openAdd = () => {
    setForm({
      vendor: "", amount: "", category: "curriculum", purchase_date: today(),
      notes: "", student_id: students[0]?.id || "", file: null,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.vendor.trim()) { toast.error("Vendor is required"); return; }
    if (!Number(form.amount) || Number(form.amount) <= 0) { toast.error("Amount must be > 0"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("vendor", form.vendor);
      fd.append("amount", form.amount);
      fd.append("category", form.category);
      fd.append("purchase_date", form.purchase_date);
      fd.append("notes", form.notes || "");
      if (form.student_id) fd.append("student_id", form.student_id);
      if (form.file) fd.append("file", form.file);
      await api.post("/receipts", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Receipt saved");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete receipt from ${r.vendor} ($${r.amount})?`)) return;
    try {
      await api.delete(`/receipts/${r.id}`);
      toast.success("Receipt removed");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const downloadFile = async (r) => {
    try {
      const { data } = await api.get(`/receipts/${r.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: r.file?.content_type || "application/octet-stream" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed");
    }
  };

  const exportCsv = () => {
    // Direct link with cookies
    window.open(`${BACKEND_URL}/api/receipts/export.csv?year=${year}`, "_blank");
  };

  const studentName = (id) => students.find((s) => s.id === id)?.name || "";
  const categoryLabel = (id) => categories.find((c) => c.id === id)?.label || id;

  const years = useMemo(() => {
    const now = new Date().getFullYear();
    return [now, now - 1, now - 2, now - 3];
  }, []);

  return (
    <div className="space-y-6" data-testid="receipts-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold">Scholarship receipts</h1>
          <p className="text-stone-500 mt-1 text-sm">Keep every curriculum, tutoring, and field-trip receipt in one place. Export at tax / reimbursement time.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="rounded-full h-10 w-32" data-testid="receipts-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={exportCsv} variant="outline" className="rounded-full border-2 border-stone-200" data-testid="receipts-export-csv">
            <Download className="w-4 h-4 mr-2" /> CSV
          </Button>
          <Button onClick={openAdd} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="receipts-add-btn">
            <Plus className="w-4 h-4 mr-2" /> Add receipt
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      {summary && (
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5" data-testid="summary-total">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-amber-800 flex items-center gap-2">
              <Wallet className="w-4 h-4" /> Total {year}
            </div>
            <div className="mt-2 font-display text-4xl font-semibold text-stone-900">{fmtMoney(summary.total)}</div>
            <div className="mt-1 text-sm text-stone-500">{summary.count} receipts</div>
          </div>
          <div className="bg-white border border-stone-200 rounded-2xl p-5 md:col-span-2">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">Spend by category</div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
              {summary.by_category.filter((c) => c.total > 0).map((c) => (
                <div key={c.category} className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2 flex items-center justify-between">
                  <span className="truncate text-stone-700">{c.label}</span>
                  <span className="font-semibold text-stone-900 shrink-0">{fmtMoney(c.total)}</span>
                </div>
              ))}
              {summary.by_category.every((c) => c.total === 0) && (
                <div className="text-sm text-stone-500 col-span-3">No receipts logged for {year} yet.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="receipts-table">
          <thead className="bg-stone-50 text-stone-500 uppercase tracking-[0.12em] text-[11px]">
            <tr>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Vendor</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Student</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-center px-4 py-3">File</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {loading && (<tr><td colSpan={7} className="text-center py-10 text-stone-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</td></tr>)}
            {!loading && rows.length === 0 && (<tr><td colSpan={7} className="text-center py-10 text-stone-400">No receipts yet. Add your first one to start tracking scholarship-eligible spending.</td></tr>)}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="hover:bg-stone-50 transition-colors" data-testid={`receipt-row-${r.id}`}>
                <td className="px-4 py-3 text-stone-700 whitespace-nowrap">{r.purchase_date}</td>
                <td className="px-4 py-3 font-medium text-stone-900">{r.vendor}</td>
                <td className="px-4 py-3 text-stone-600">{categoryLabel(r.category)}</td>
                <td className="px-4 py-3 text-stone-600">{r.student_id ? studentName(r.student_id) : <span className="text-stone-400">Whole family</span>}</td>
                <td className="px-4 py-3 text-right font-semibold text-stone-900">{fmtMoney(r.amount)}</td>
                <td className="px-4 py-3 text-center">
                  {r.file ? (
                    <button onClick={() => downloadFile(r)} className="text-amber-600 hover:text-amber-700 inline-flex items-center gap-1 text-xs" data-testid={`receipt-download-${r.id}`}>
                      <FileText className="w-3.5 h-3.5" /> View
                    </button>
                  ) : (<span className="text-stone-300 text-xs">—</span>)}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => del(r)} className="w-7 h-7 rounded-full bg-red-50 text-red-600 hover:bg-red-500 hover:text-white ring-1 ring-red-200 inline-grid place-items-center" data-testid={`receipt-delete-${r.id}`} title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Add receipt</DialogTitle>
            <DialogDescription>Track a scholarship-eligible expense. Attach the receipt (PDF or photo) so you can find it later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Vendor</Label>
                <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="Rainbow Resource Center" className="mt-2 rounded-xl h-11" data-testid="receipt-vendor" />
              </div>
              <div>
                <Label>Amount (USD)</Label>
                <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="127.45" className="mt-2 rounded-xl h-11" data-testid="receipt-amount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-2 rounded-xl h-11" data-testid="receipt-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Purchase date</Label>
                <Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="mt-2 rounded-xl h-11" data-testid="receipt-date" />
              </div>
            </div>
            <div>
              <Label>Student (optional)</Label>
              <Select value={form.student_id || "__none__"} onValueChange={(v) => setForm({ ...form, student_id: v === "__none__" ? "" : v })}>
                <SelectTrigger className="mt-2 rounded-xl h-11" data-testid="receipt-student"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Whole family</SelectItem>
                  {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 rounded-xl min-h-[60px]" placeholder="Grade 3 math workbook + teacher guide" data-testid="receipt-notes" />
            </div>
            <div>
              <Label>Receipt file (optional, PDF or image, 5 MB max)</Label>
              <div className="mt-2 flex items-center gap-2">
                <input type="file" accept="application/pdf,image/*" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} className="text-sm text-stone-500 file:mr-3 file:rounded-full file:border-0 file:bg-amber-50 file:text-amber-700 file:px-3 file:py-1.5 file:font-medium hover:file:bg-amber-100" data-testid="receipt-file" />
                {form.file && <span className="text-xs text-stone-500 truncate">{form.file.name}</span>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="receipt-save">
              {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>) : (<><Upload className="w-4 h-4 mr-2" />Save receipt</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
